import {memo, useState} from 'react';

import styled from 'styled-components';

import {media} from '~/shared/theme/media';
import {trackEvent} from '~/shared/services/analytics';
import {createLogger} from '~/shared/logging';
import CardBase from '~/shared/components/TenbisCard/CardBase';
import DefaultBackground from '~/shared/components/TenbisCard/DefaultBackground';
import WalleBackground from '~/shared/components/TenbisCard/WalleBackground';

const logger = createLogger('TenbisCard');

const Root = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 82 / 53;
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 16px;
  box-shadow: ${({theme}) => theme.shadows.cardElevation};
  overflow: hidden;
  ${media.minLargeMobile`
    width: 328px;
    height: 212px;
  `}
`;

type TenbisCardProps = {
  cardHolderName: string;
  cardNumber: string;
  expirationYear?: string;
  expirationMonth?: string;
  cvv?: string;
  isActive: boolean;
  isRevealed: boolean;
  onShowDetailsClick?: (() => void) | (() => Promise<void>);
  onHideDetailsClick?: (() => void) | (() => Promise<void>);
  onActivateCardClick?: () => void;
  className?: string;
  isWalle?: boolean;
};

const TenbisCard = ({
  cardHolderName,
  cardNumber,
  expirationMonth,
  expirationYear,
  cvv,
  onShowDetailsClick,
  onHideDetailsClick,
  onActivateCardClick,
  className,
  isActive,
  isRevealed,
  isWalle,
}: TenbisCardProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const showDetailsHandler = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      trackEvent('mcDetails_click');
      if (!isRevealed) {
        if (onShowDetailsClick) {
          await onShowDetailsClick();
        }
      }
      if (isRevealed && onHideDetailsClick) {
        if (onHideDetailsClick) {
          await onHideDetailsClick();
        }
      }
    } catch (err) {
      logger.warn('error while getting moneycard details', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const expiry = expirationYear && expirationMonth && `${expirationMonth}/${expirationYear}`;

  const Background = isWalle ? WalleBackground : DefaultBackground;

  return (
    <Root className={className}>
      <Background isActive={isActive} />
      <CardBase
        isWalle={isWalle}
        cardHolderName={cardHolderName}
        hasError={hasError}
        isActive={isActive}
        isRevealed={isRevealed}
        isLoading={isLoading}
        cardNumber={cardNumber}
        cvv={cvv}
        expiry={expiry}
        showDetailsHandler={showDetailsHandler}
        onActivateCardClick={onActivateCardClick}
      />
    </Root>
  );
};

export default memo(TenbisCard);
